import './NachrichtSendenModal.css';

import {
  Modal,
  Stack,
  Input,
  Button,
  Spinner,
  TextArea,
  FormRow
} from '@efast_public/fjd-component-library';
import { useState } from 'react';
import { useSendMessage } from '../../api/hooks/useSendMessage';
import { useUploadFiles } from '../../shared/hooks/useUploadFiles';
import UploadsList from '../../pages/shared/UploadsList';
import { useProfile } from '../../shared/hooks/AuthContext';
import { Bescheidstatus } from '../../api/models/Status/Bescheidstatus';
import { useSubmissionsAsNachrichten } from '../../api/hooks/useSubmissionsAsNachrichten';
import { NachrichtType } from '../../api/models/Nachrichten/NachrichtModel';
import { ActionType } from '../../api/models/UploadFiles/FileUploadAction';
import { maxSizeZBPMessage } from '../../shared/utils/FileRestriction';
import {
  errorTextOnNotAvailableBundIdConnection,
  isApplicationInFederalNetwork
} from '../../shared/utils/mandant';
import { CaseDetailsModel } from '../../api/models/Case/CaseDetailsModel';
import { KommunikationRequestModel } from './KommunikationRequestModel';
import { useNotification } from '../../shared/notification/useNotification';
import { AxiosError } from 'axios';

interface NachrichtSendenModalProps {
  readonly onClose: () => void;
  readonly caseId: string;
  readonly caseVersion: string;
  readonly useSubmissionsAsNachrichtenError?: AxiosError<unknown, any> | null; // any is defined by swr, cannot give a type
  readonly isOpen: boolean;
  readonly caseDetails: CaseDetailsModel;
}

function NachrichtSendenModal({
  caseDetails,
  ...props
}: NachrichtSendenModalProps) {
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const { notifyUser } = useNotification();
  const { sendMessage, loadingSend } = useSendMessage();
  const { uploadState, dispatch } = useUploadFiles();
  const { profile, error: useProfileError } = useProfile();
  const { refetch: mutate } = useSubmissionsAsNachrichten({
    caseId: caseDetails.caseId
  });

  const resetValues = () => {
    setSubject('');
    setText('');
    dispatch({ type: ActionType.REMOVE_ALL_ADDITIONAL_FILES });
  };

  const onClose = () => {
    resetValues();
    props.onClose();
  };

  const hasError = props.useSubmissionsAsNachrichtenError || useProfileError;
  const primaryDisabled =
    !(text.length > 0 && subject.length > 0) ||
    loadingSend ||
    new TextEncoder().encode(text).length > maxSizeZBPMessage;

  const handleSend = () => {
    if (isApplicationInFederalNetwork()) {
      const payload: KommunikationRequestModel = {
        absender: `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`,
        betreff: subject,
        nachrichttext: text,
        attachments: uploadState.bescheidUpload
          ? [uploadState.bescheidUpload, ...uploadState.additionalFiles]
          : uploadState.additionalFiles,
        nachrichtType: NachrichtType.NACHRICHT_DIGITAL,
        bescheidstatus: Bescheidstatus.NOCHNICHTBESCHIEDEN,
        caseVersion: props.caseVersion
      };
      sendMessage({ payload, caseId: props.caseId })
        .then(() => onClose())
        .catch(() => {})
        .finally(() => mutate());
    } else {
      notifyUser({
        intent: 'error',
        title: errorTextOnNotAvailableBundIdConnection
      });
    }
  };

  return (
    <Modal
      appElement={document.querySelector('.fjd-base-layout') as HTMLElement}
      closeOnBackdropClick={false}
      open={props.isOpen}
      onClose={onClose}
    >
      <Modal.Header id="NachrichtSendenModal-header">
        Nachricht versenden
      </Modal.Header>
      <Modal.Content>
        {loadingSend ? (
          <Spinner />
        ) : (
          <Stack direction="column" spacing="xs">
            <FormRow label="Absender:in der Nachricht">
              <Input
                id="absender"
                value={`${profile?.firstName ?? '?'} ${profile?.lastName ?? '?'}`}
                disabled
              />
            </FormRow>
            <FormRow label="Auftragsnummer & Antragstitel">
              <Input
                id="auftragsnummer"
                minLength={1}
                value={`${caseDetails.auftragsnummer} • ${caseDetails.name}`}
                disabled
              />
            </FormRow>
            <FormRow label="Betreff">
              <Input
                id="betreff"
                data-testid={'betreff'}
                minLength={1}
                onChange={(event) => setSubject(event.target.value)}
                value={subject}
                placeholder="Betreff"
              />
            </FormRow>
            <FormRow label="Nachrichtentext">
              <TextArea
                id="nachrichtentext"
                data-testid={'nachrichtentext'}
                minLength={1}
                rows={6}
                onChange={(event) => setText(event.target.value)}
                value={text}
              />
            </FormRow>
            <UploadsList
              uploadFiles={uploadState}
              dispatch={dispatch}
              hasUploadButtonEnabled={true}
              uploadButtonTextZeroFilesUploaded={
                'Anlage hinzufügen (max. 2 MB)'
              }
              uploadButtonTextAtLeastOneFileUploaded={
                'Weitere Anlage hinzufügen'
              }
              showPreview={true}
              hasIconsRightAligned={true}
            />
          </Stack>
        )}
      </Modal.Content>
      <Modal.Footer>
        {hasError ? (
          <Button variant="primary" onClick={onClose}>
            Schließen
          </Button>
        ) : (
          <>
            <Button
              variant="secondary-outline"
              disabled={loadingSend}
              onClick={onClose}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              disabled={primaryDisabled}
              onClick={handleSend}
            >
              Nachricht absenden
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default NachrichtSendenModal;
